/*
 * The outbox (spec 08 §2.4, §4, §6): every message the composer hands over is written to
 * IndexedDB before the composer clears, and stays there until the server has acknowledged it —
 * across a reload, a crash, a lost socket and a server restart. The first `holdMs` of a *posted*
 * message's life are the undo window: nothing has left the device, so taking it back is a deletion
 * and no notification was ever rung. The window is off unless the account asked for one (A-08 §5),
 * and an edit or a delete is never held at all — neither has a way back, so a hold on them would be
 * latency with no product behind it.
 *
 * One tab of a session dispatches (the Web Locks leader of leader.ts); the others enqueue and
 * learn about the leader's progress over `BroadcastChannel("teambase-outbox")`. Per target the
 * queue is FIFO and never reordered; a failed head pauses its own target and no other. Sending is
 * idempotent on the server side by `clientMsgId`, so a retry after a lost ack is harmless — and
 * after a reconnect the socket calls `resume()` only once the catch-up has been applied, so the
 * echo of an already-landed message is collapsed instead of double-rendered (protocol.md §2.10).
 *
 * Leading is the right to dispatch, so it is held only by a tab that can: `pause()` stands down
 * from the election and `resume()` joins it again, and a tab whose socket is gone never keeps a tab
 * that has one from sending. `inflight` in turn is only ever the state of an attempt *this* tab is
 * making *now*. Whoever stops leading abandons its attempts — the items go back to `queued`, and
 * whatever those requests answer later is read as the answer to an attempt that no longer counts
 * (`isLive`). That is what keeps `inflight`, which `due()` never dispatches, from being a state an
 * item can stay in: no lost reply, no dead leader and no reconnect leaves one there. An abandoned
 * attempt costs the item nothing else: the transient failures it may have before it stops and asks
 * the user are counted in `failures`, which only a failure raises.
 *
 * What an item becomes on the wire is not the outbox's business: each key prefix has a transport its
 * module registered (`transport.ts`). And one answer is neither a success nor a failure: a send the
 * server set aside for its author (an AI chat prompt that crossed another owner's). That item waits
 * in `needsAuthor`, holding its target, until the author sends it anyway or changed (`resend`), takes
 * the text back into the box (`discardToDraft`) or throws it away (`discard`).
 */

import { useCallback, useSyncExternalStore } from "react"
import {
	BACKOFF_CAP_MS,
	backoffMs,
	classify,
	due,
	MAX_TRANSIENT_ATTEMPTS,
	type Failure,
} from "./dispatch.ts"
import type { DraftStore } from "./draftStore.ts"
import { holdLeadership, type LeaderLocks, type Leadership } from "./leader.ts"
import { transportFor, type OutboxTransport } from "./transport.ts"
import type { AttachmentRef, DraftFields, OutboxItem, OutboxPayload, TargetKey } from "./types.ts"

export const OUTBOX_CHANNEL = "teambase-outbox"

/**
 * What a listener learns beyond the list: a delivery, a refusal whose text went back, or a message
 * taken back inside its window.
 *
 * `undone` carries the message itself and not only its id, because its one consumer is the composer
 * it was typed in: the text and the files go back into the box, and the box is not the outbox's to
 * read from the disk.
 */
export type OutboxNotice =
	| { kind: "sent"; target: TargetKey; clientMsgId: string }
	| { kind: "refused"; target: TargetKey; clientMsgId: string; key: string }
	| { kind: "needsAuthor"; target: TargetKey; clientMsgId: string; key: string }
	| {
			kind: "undone"
			target: TargetKey
			clientMsgId: string
			markdown: string
			attachments: AttachmentRef[]
	  }

export type OutboxListener = (items: readonly OutboxItem[], notice?: OutboxNotice) => void

export interface OutboxDeps {
	/** Where refused text returns to, and whose database the items share. */
	store: DraftStore
	/**
	 * Sends every item, in place of the transports the modules registered (`transport.ts`) — for a
	 * test, which has no module to register one.
	 */
	transport?: OutboxTransport
	/** The undo window, read per item so a changed setting applies to the next send. */
	holdMs: () => number
	now?: () => number
	random?: () => number
	/** `undefined` = the browser's Web Locks; `null` = no election (a single context). */
	locks?: LeaderLocks | null
}

export class Outbox {
	private list: OutboxItem[] = []
	private readonly listeners = new Set<OutboxListener>()
	private timer: ReturnType<typeof setTimeout> | undefined
	private isLeader = false
	/** Whether the owner has said the socket is down; it decides only whether this tab competes. */
	private paused = false
	private started = false
	/** The one start, so a caller that must act after the disk was read can wait for it. */
	private starting: Promise<void> | undefined
	private leadership: Leadership | undefined
	private channel: BroadcastChannel | undefined
	private unloadGuarded = false
	private readonly now: () => number
	private readonly random: () => number
	private readonly deps: OutboxDeps

	constructor(deps: OutboxDeps) {
		this.deps = deps
		this.now = deps.now ?? (() => Date.now())
		this.random = deps.random ?? (() => Math.random())
	}

	/** Loads what the disk holds, joins the election and starts dispatching when elected. */
	start(): Promise<void> {
		this.starting ??= this.startOnce()
		return this.starting
	}

	private async startOnce(): Promise<void> {
		if (this.started) return
		this.started = true
		await this.deps.store.ready
		await this.reload()
		if (typeof BroadcastChannel !== "undefined") {
			this.channel = new BroadcastChannel(OUTBOX_CHANNEL)
			this.channel.addEventListener("message", () => void this.reload())
		}
		this.leadership = holdLeadership({
			...(this.deps.locks === undefined ? {} : { locks: this.deps.locks }),
			onAcquire: () => {
				this.isLeader = true
				// What the last leader was sending is this tab's to send now, and the disk is the only
				// place sure to know of it: a leader that died mid-request told nobody, and a tab that
				// was frozen heard no broadcast.
				void this.reload()
				this.changed()
			},
			onRelease: () => {
				this.isLeader = false
				this.abandonAttempts()
				this.changed()
			},
		})
		if (!this.paused) this.leadership.compete()
	}

	/**
	 * Throws every item away, on the disk and in memory — nothing is sent, nothing returns to a
	 * draft. Its one caller is the account claim of this device (core/session/device.ts): these
	 * items would otherwise be dispatched as whoever signs in next. The store's own `discardAll`
	 * empties the same database, so the disk is cleared there and this drops only the list.
	 */
	async discardAll(): Promise<void> {
		await this.starting
		this.list = []
		this.changed()
	}

	stop(): void {
		this.started = false
		this.starting = undefined
		this.cancelTimer()
		this.leadership?.stop()
		this.leadership = undefined
		this.channel?.close()
		this.channel = undefined
		this.setUnloadGuard(false)
	}

	items(): readonly OutboxItem[] {
		return this.list
	}

	leader(): boolean {
		return this.isLeader
	}

	/** True while leaving the page would lose something the server has not confirmed. */
	guardsUnload(): boolean {
		return this.unloadGuarded
	}

	subscribe(listener: OutboxListener): () => void {
		this.listeners.add(listener)
		return () => {
			this.listeners.delete(listener)
		}
	}

	/**
	 * Adds an item, durable before this resolves; `draftKey` is the draft it was written in, gone
	 * in the same transaction. A posted message is `held` for the undo window, or `queued` at once
	 * when the window is zero; an edit and a delete are always `queued`, because the window's whole
	 * product is `undo()`, and `undo()` can only put a *new* message's text back into a composer.
	 */
	async enqueue(
		target: TargetKey,
		payload: OutboxPayload,
		draftKey?: TargetKey,
	): Promise<OutboxItem> {
		const now = this.now()
		const holdMs = payload.kind === "post" ? Math.max(0, this.deps.holdMs()) : 0
		const order = this.list.reduce(
			(max, i) => (i.target === target ? Math.max(max, i.order) : max),
			0,
		)
		const item: OutboxItem = {
			clientMsgId: crypto.randomUUID(),
			target,
			order: order + 1,
			payload,
			createdAt: now,
			sendAfter: now + holdMs,
			attempts: 0,
			failures: 0,
			nextAttemptAt: now,
			state: holdMs > 0 ? "held" : "queued",
		}
		const db = this.deps.store.database()
		if (db !== undefined) await db.enqueue(item, draftKey)
		if (draftKey !== undefined) this.deps.store.forget(draftKey)
		this.list = [...this.list, item]
		this.broadcast()
		this.changed()
		return item
	}

	/**
	 * Takes a held item back: the text returns to its draft, the item is gone, and the composer it
	 * was typed in hears `undone` so it can put the text and the files back into the box. Only a
	 * post is ever held, so `payload.markdown` is always there.
	 */
	async undo(clientMsgId: string): Promise<OutboxItem | undefined> {
		const item = this.find(clientMsgId)
		if (item === undefined || item.state !== "held" || item.payload.kind !== "post") {
			return undefined
		}
		const dropped = this.drop(item)
		await this.restoreDraft(item)
		this.changed({
			kind: "undone",
			target: item.target,
			clientMsgId,
			markdown: item.payload.markdown,
			attachments: item.payload.attachments,
		})
		await dropped
		return item
	}

	/** Undoes the most recently held item of a target — Ctrl+Z in an empty composer. */
	undoLatest(target: TargetKey): Promise<OutboxItem | undefined> {
		const held = this.list.filter((i) => i.target === target && i.state === "held")
		const latest = held.at(-1)
		return latest === undefined ? Promise.resolve(undefined) : this.undo(latest.clientMsgId)
	}

	/**
	 * Sends a failed item, or one set aside for its author, again under the same `clientMsgId` — the
	 * server wrote nothing the first time, so there is nothing to duplicate. `extra` is merged into
	 * what the post carries beside its text: how the author answers ("send it anyway", "make it a
	 * note"). The item keeps its place at the head of its target.
	 */
	async resend(clientMsgId: string, extra?: DraftFields): Promise<void> {
		const item = this.find(clientMsgId)
		if (item === undefined || (item.state !== "failed" && item.state !== "needsAuthor")) return
		const payload: OutboxPayload =
			extra !== undefined && item.payload.kind === "post"
				? { ...item.payload, extra: { ...item.payload.extra, ...extra } }
				: item.payload
		const next: OutboxItem = {
			...item,
			payload,
			state: "queued",
			attempts: 0,
			failures: 0,
			nextAttemptAt: this.now(),
		}
		delete next.lastError
		const replaced = this.replace(next)
		this.changed()
		await replaced
	}

	/** "Try again" on a failed item: back to the queue with a clean slate, budget included. */
	retry(clientMsgId: string): Promise<void> {
		return this.resend(clientMsgId)
	}

	/**
	 * Throws an item away without putting its text anywhere — the author's own *Discard*, asked and
	 * confirmed by the surface. An item on the wire is left alone: its answer decides.
	 */
	async discard(clientMsgId: string): Promise<void> {
		const item = this.find(clientMsgId)
		if (item === undefined || item.state === "inflight") return
		const dropped = this.drop(item)
		this.changed()
		await dropped
	}

	/** "Back to the draft" on a failed item, or on one set aside for its author (*Edit*). */
	async discardToDraft(clientMsgId: string): Promise<void> {
		const item = this.find(clientMsgId)
		if (item === undefined || item.state === "inflight") return
		const dropped = this.drop(item)
		this.changed()
		await dropped
		await this.restoreDraft(item)
	}

	/**
	 * The server has the message — seen in a `chat.message.new` echo or a catch-up page carrying
	 * this `clientMsgId` — so a retry whose ack was lost is not needed any more.
	 */
	async acknowledged(clientMsgId: string): Promise<void> {
		const item = this.find(clientMsgId)
		if (item === undefined) return
		const dropped = this.drop(item)
		this.changed({ kind: "sent", target: item.target, clientMsgId })
		await dropped
	}

	/**
	 * The socket closed: nothing goes out until `resume`, and the lead goes to a tab that can send.
	 * No request of this tab survives its socket, so what was in flight is queued again at once
	 * (`onRelease`) instead of waiting for settlements that may never come.
	 */
	pause(): void {
		this.paused = true
		this.leadership?.standDown()
	}

	/**
	 * The socket is back and the catch-up has been applied: this tab asks for the lead again, and the
	 * heads go out once it has it.
	 */
	resume(): void {
		this.paused = false
		this.leadership?.compete()
	}

	private find(clientMsgId: string): OutboxItem | undefined {
		return this.list.find((i) => i.clientMsgId === clientMsgId)
	}

	/**
	 * Re-reads the disk: another tab enqueued, sent or undid something. An item this tab is
	 * sending keeps its in-memory state; an item the disk calls `inflight` and nobody here is
	 * sending is another tab's attempt — the leader's, or one a dead leader left behind — and is
	 * `queued` as far as this tab is concerned: should it ever lead, the item is its to send.
	 */
	private async reload(): Promise<void> {
		const db = this.deps.store.database()
		if (db === undefined) return
		let rows: OutboxItem[]
		try {
			rows = await db.loadOutbox()
		} catch {
			return
		}
		this.list = rows.map((row) => {
			const mine = this.find(row.clientMsgId)
			if (mine?.state === "inflight") return mine
			return row.state === "inflight" ? { ...row, state: "queued" } : row
		})
		this.changed()
	}

	/**
	 * This tab has stopped dispatching, so its attempts no longer count: every `inflight` item is
	 * `queued` again, on the disk too, where the next leader reads it. The requests themselves may
	 * still answer; `isLive` is what makes those answers harmless.
	 */
	private abandonAttempts(): void {
		for (const item of this.list) {
			if (item.state === "inflight") void this.replace({ ...item, state: "queued" })
		}
	}

	/** True while `attempt` is the one this tab is making: not abandoned, not retired, not superseded. */
	private isLive(attempt: OutboxItem): boolean {
		const current = this.find(attempt.clientMsgId)
		return current?.state === "inflight" && current.attempts === attempt.attempts
	}

	/**
	 * The memory list changes at once — what the interface and the unload guard see never waits
	 * for the disk — and the row follows; the other tabs hear about it once it is on disk.
	 */
	private async replace(item: OutboxItem): Promise<void> {
		this.list = this.list.map((i) => (i.clientMsgId === item.clientMsgId ? item : i))
		try {
			await this.deps.store.database()?.putOutbox(item)
		} catch {
			// The memory copy carries the state; the next successful write catches the disk up.
		}
		this.broadcast()
	}

	private async drop(item: OutboxItem): Promise<void> {
		this.list = this.list.filter((i) => i.clientMsgId !== item.clientMsgId)
		try {
			await this.deps.store.database()?.deleteOutbox(item.clientMsgId)
		} catch {
			// A row that would not delete is re-sent at the next start and deduplicated there.
		}
		this.broadcast()
	}

	/**
	 * Puts a message that did not leave back into its draft — before whatever was typed into the box
	 * since it was sent, never over it (the composer keeps text typed right after Enter): the message
	 * first, a blank line, then the rest, with both sets of files.
	 */
	private async restoreDraft(item: OutboxItem): Promise<unknown> {
		const p = item.payload
		if (p.kind === "delete") return
		const key: TargetKey = p.kind === "post" ? item.target : `e:${p.messageId}`
		const store = this.deps.store
		await store.ready
		const now = store.get(key)
		const typed = now?.markdown ?? ""
		const markdown =
			typed === "" || typed === p.markdown
				? p.markdown
				: `${p.markdown.replace(/\n+$/, "")}\n\n${typed}`
		const kept = (now?.attachments ?? []).filter((a) => !p.attachments.some((b) => b.id === a.id))
		return store.put(key, markdown, [...p.attachments, ...kept], now?.fields)
	}

	private broadcast(): void {
		this.channel?.postMessage({ type: "changed" })
	}

	private changed(notice?: OutboxNotice): void {
		this.updateUnloadGuard()
		for (const l of this.listeners) l(this.list, notice)
		this.schedule()
	}

	private cancelTimer(): void {
		if (this.timer !== undefined) clearTimeout(this.timer)
		this.timer = undefined
	}

	/** One timer, always aimed at the earliest moment something becomes due — in the tab that leads. */
	private schedule(): void {
		this.cancelTimer()
		if (!this.started || !this.isLeader) return
		const now = this.now()
		const { ready, nextAt } = due(this.list, now)
		if (ready.length > 0) this.timer = setTimeout(() => this.tick(), 0)
		else if (nextAt !== undefined) this.timer = setTimeout(() => this.tick(), nextAt - now)
	}

	private tick(): void {
		this.timer = undefined
		if (!this.isLeader) return
		const { ready } = due(this.list, this.now())
		for (const item of ready) void this.send(item)
		this.schedule()
	}

	/**
	 * One attempt. Its number (`attempts`) is its identity: by the time the request settles the item
	 * may have been abandoned and sent again, or retired by its echo, and then only a success still
	 * means something — the server has the message, whichever attempt carried it, so the item goes
	 * if it is still there. A failure of an attempt that is no longer live is nobody's: acting on it
	 * would back a newer attempt off, or write a delivered item back to the disk.
	 */
	private async send(item: OutboxItem): Promise<void> {
		const attempt: OutboxItem = { ...item, state: "inflight", attempts: item.attempts + 1 }
		const marked = this.replace(attempt)
		this.changed()
		await marked
		if (!this.isLive(attempt)) return
		const transport = this.deps.transport ?? transportFor(attempt.target)
		if (transport === undefined) {
			await this.waitForTransport(attempt)
			return
		}
		try {
			await transport(attempt)
		} catch (err) {
			if (this.isLive(attempt)) await this.failed(attempt, classify(err))
			return
		}
		if (this.find(attempt.clientMsgId) === undefined) return
		const dropped = this.drop(attempt)
		this.changed({ kind: "sent", target: attempt.target, clientMsgId: attempt.clientMsgId })
		await dropped
	}

	/**
	 * No module of this build sends this key: the one that owns it is switched off here, or not
	 * loaded yet. The item stays exactly as durable as it was and is asked about again after the
	 * longest backoff — without spending its failure budget, because nothing failed.
	 */
	private async waitForTransport(attempt: OutboxItem): Promise<void> {
		const replaced = this.replace({
			...attempt,
			state: "queued",
			nextAttemptAt: this.now() + BACKOFF_CAP_MS,
		})
		this.changed()
		await replaced
	}

	/**
	 * A transient failure backs off and tries again, until the failures run out and the item asks
	 * the user; a refusal is the server's final word — the item is gone and its text is back in the
	 * draft, where the user will look for it (spec 08 §6.7, §6.13); and a send the server set aside
	 * for its author waits for the author, text and all, holding its target.
	 *
	 * What the budget counts is failures and not attempts: an attempt this tab abandoned was never
	 * one, so a week of reconnects must not bring a message one failure away from "Could not be
	 * sent" before anything has gone wrong with it.
	 */
	private async failed(item: OutboxItem, failure: Failure): Promise<void> {
		const now = this.now()
		const failures = (item.failures ?? 0) + 1
		if (failure.needsAuthor === true) {
			const replaced = this.replace({
				...item,
				state: "needsAuthor",
				lastError: { key: failure.key, retryable: false },
			})
			this.changed({
				kind: "needsAuthor",
				target: item.target,
				clientMsgId: item.clientMsgId,
				key: failure.key,
			})
			await replaced
		} else if (failure.retryable && failures < MAX_TRANSIENT_ATTEMPTS) {
			const delay = Math.max(failure.retryAfterMs ?? 0, backoffMs(failures, this.random))
			const replaced = this.replace({
				...item,
				state: "queued",
				failures,
				nextAttemptAt: now + delay,
				lastError: { key: failure.key, retryable: true },
			})
			this.changed()
			await replaced
		} else if (failure.retryable) {
			const replaced = this.replace({
				...item,
				state: "failed",
				failures,
				lastError: { key: failure.key, retryable: true },
			})
			this.changed()
			await replaced
		} else {
			const dropped = this.drop(item)
			await this.restoreDraft(item)
			this.changed({
				kind: "refused",
				target: item.target,
				clientMsgId: item.clientMsgId,
				key: failure.key,
			})
			await dropped
		}
	}

	/**
	 * `beforeunload` only while something un-acked would be lost by leaving: never for a held item,
	 * never for one waiting on its author's decision — both lie on the disk and nothing is on its way
	 * — and never for a draft; unless storage is memory-only, where a held item is exactly as lost
	 * as a queued one (spec 08 §4, §6.9, A19).
	 */
	private updateUnloadGuard(): void {
		const memoryOnly = this.deps.store.database() === undefined
		this.setUnloadGuard(
			this.list.some((i) => memoryOnly || (i.state !== "held" && i.state !== "needsAuthor")),
		)
	}

	private setUnloadGuard(on: boolean): void {
		if (on === this.unloadGuarded) return
		this.unloadGuarded = on
		if (on) window.addEventListener("beforeunload", warnBeforeUnload)
		else window.removeEventListener("beforeunload", warnBeforeUnload)
	}
}

function warnBeforeUnload(event: BeforeUnloadEvent): void {
	event.preventDefault()
	// Chromium still wants the legacy property to show the dialog.
	event.returnValue = ""
}

let installed: Outbox | undefined

/** Creates and starts the application's outbox; the previous one, if any, stops. */
export function installOutbox(deps: OutboxDeps): Outbox {
	installed?.stop()
	installed = new Outbox(deps)
	void installed.start()
	return installed
}

export function currentOutbox(): Outbox | undefined {
	return installed
}

const NO_ITEMS: readonly OutboxItem[] = []

/** One fact about an outbox as React state; `read` must answer the same value until it changes. */
function useOutboxValue<T>(outbox: Outbox | undefined, read: (outbox: Outbox | undefined) => T): T {
	const subscribe = useCallback(
		(listener: () => void) => (outbox === undefined ? () => {} : outbox.subscribe(listener)),
		[outbox],
	)
	return useSyncExternalStore(subscribe, () => read(outbox))
}

/** The items of one outbox as React state; an absent outbox is an empty list. */
export function useOutboxItems(
	outbox: Outbox | undefined = currentOutbox(),
): readonly OutboxItem[] {
	return useOutboxValue(outbox, (o) => o?.items() ?? NO_ITEMS)
}

/** Whether this tab is the one that dispatches, as React state; an absent outbox leads nothing. */
export function useOutboxLeader(outbox: Outbox | undefined = currentOutbox()): boolean {
	return useOutboxValue(outbox, (o) => o?.leader() ?? false)
}
